import { useEffect } from 'react';
import { Storage } from '../lib/storage';
import {
  fetchPlayers,
  persistPlayers,
  ratingCommitsAreServerAuthoritative,
} from '../lib/api/players';
import { errMsg } from '../lib/errors';
// The log stores codes, not Norwegian sentences (#519): lib/tournamentLog.ts.
import { logEntry } from '../lib/tournamentLog';
import { catchUpSessionRatings } from '../lib/api/tournamentResults';
import { nextDisplayTournamentId } from '../engine/padel24Results';
import { isLiveTournamentView } from '../app/views/types';
import { useTournamentResultRecording } from './useTournamentResultRecording';
import {
  activityCommandsAreServerAuthoritative,
  deleteActivities,
  upsertActivity,
} from '../lib/api/activities';
import { SaveRefused, saveWithRetry } from '../lib/saveWithRetry';
import type { TournamentKey } from './useTournaments';

// ─────────────────────────────────────────────────────────────────────
// useTournamentLifecycle — the tournament-lifecycle orchestration cluster lifted
// VERBATIM out of Padel24AppMain (extracted from the former monolith) as a
// god-component collapse slice (PR-BF). 18 handlers + the Padel24-preview->commit
// effect. Called AFTER useDerivations in Padel24AppMain so effectivePlayerDB is in
// scope (TDZ) — recordResultForTournament / recordKMResultForTournament read it.
//
// ⚠️ archiveTournament is the single most correctness-critical handler: it
// PERMANENTLY commits ELO to the global player DB. For Padel24/Americano it
// syncs elo/matchesPlayed/lastMatchAt from t.players; for KM/seriespill it sums
// the per-match teamEloChange deltas per player. Pinned by golden-fixture
// characterization tests (tests/state-tournament-lifecycle.test.ts). That commit
// GATES the archive (#273): it is awaited before the tournament counts as
// archived, and a failed write rolls the archive blob back and returns an error
// instead of archiving a tournament whose ratings never landed.
//
// persistPaused is private to the hook (the sole writer of paused_tournaments_v1
// here). R1/§13.20: all writes are explicit Storage-first writers — this hook
// never touches the persisted-slice loaded-flag refs; the blanket-persist effects
// in the slice hooks stay the sole flag-gated writers. Engine bindings are
// imported directly (identity-equal to the Padel24Engine.<ns>.<fn> static refs).
// ─────────────────────────────────────────────────────────────────────
export interface UseTournamentLifecycleDeps {
  tournament: any;
  setTournament: (v: any) => void;
  pausedTournaments: any;
  setPausedTournaments: (v: any) => void;
  archive: any;
  setArchive: (v: any) => void;
  /** The raw player list. Needed because the archive's ELO commit must build the
   *  updated list OUTSIDE a state updater — see archiveTournament (#273). */
  allPlayers: any;
  /**
   * The R5 sync mirror from usePlayers. The archive commit builds its next list
   * OUTSIDE the React updater (#273), so it must not read `allPlayers` from the
   * closure — that is the last render's value, and the updater's `prev` was not.
   */
  allPlayersRef: { current: any[] };
  setAllPlayers: (v: any) => void;
  activities: any;
  adoptServerActivity: (saved: any) => void;
  persistActivities: (list: any) => any;
  view: any;
  setView: (v: any) => void;
  setToast: (v: any) => void;
  showToast: (kind: any, msg: any) => void;
  /**
   * From useTournaments (#1305): a write of `paused_tournaments_v1` that did
   * not land is reported there, where the reload of the stored list lives.
   */
  reportTournamentSaveFailure: (key: TournamentKey, error: unknown) => void;
  effectivePlayerDB: any;
  profileReturn: any;
  setProfileReturn: (v: any) => void;
  setProfilePlayerId: (v: any) => void;
  setSingleMatchesReturnView: (v: any) => void;
  setSerieHistoryFocus: (v: any) => void;
  setActivitiesOpenId: (v: any) => void;
}

export function useTournamentLifecycle({
  tournament,
  setTournament,
  pausedTournaments,
  setPausedTournaments,
  archive,
  setArchive,
  allPlayers,
  allPlayersRef,
  setAllPlayers,
  activities,
  adoptServerActivity,
  persistActivities,
  view,
  setView,
  setToast,
  showToast,
  reportTournamentSaveFailure,
  effectivePlayerDB,
  profileReturn,
  setProfileReturn,
  setProfilePlayerId,
  setSingleMatchesReturnView,
  setSerieHistoryFocus,
  setActivitiesOpenId,
}: UseTournamentLifecycleDeps) {
  const handleWizardComplete = (engineState) => {
    // If there is an active tournament, park it first so we can start a new one.
    // The parked one can be resumed later from the start page.
    parkCurrentTournament();
    const displayId = nextDisplayTournamentId(
      [...pausedTournaments, ...archive.map((a) => a.data || {}), tournament].filter(Boolean),
    );
    setTournament({ ...engineState, displayId });
    setView('tournament');
  };
  const savePreviewGroupsToActivity = (t) => {
    if (!t || !t.previewActivityId || !Array.isArray(t.groups)) return;
    const order = t.groups.flatMap((g) => (g.players || []).map((p) => p.id));
    if (!order.length) return;
    const stored = (activities || []).find((a) => a.id === t.previewActivityId);
    const saved = {
      ...(stored || {}),
      config: {
        ...(stored?.config || {}),
        p24PreviewGroupOrder: order,
        // Save the draw itself + the player set it was made for, so that
        // phase 1 is NOT drawn at random every time you enter/leave preview.
        p24PreviewMatches: Array.isArray(t.matches) ? t.matches : null,
        p24PreviewPlayerIds: (t.players || []).map((p) => p.id),
        p24PreviewGroupsEdited: !!t.groupsEditedSinceDraw,
      },
    };
    if (activityCommandsAreServerAuthoritative()) {
      // STILL NOT AWAITED — the caller is a navigation handler and this is a
      // convenience save, so blocking the screen on it would be worse than the
      // save being late. What CHANGES is that a failure is no longer silent:
      // it used to vanish into persistActivities' swallowed catch, and the draw
      // would simply be re-rolled next time with nothing to explain why.
      void saveWithRetry(() => upsertActivity(String(t.previewActivityId), saved))
        .then((res) => adoptServerActivity(res.activity))
        .catch((e) =>
          showToast('error', e instanceof SaveRefused ? e.message : 'Kunne ikke lagre trekningen.'),
        );
      return;
    }
    persistActivities((activities || []).map((a) => (a.id === t.previewActivityId ? saved : a)));
  };
  const parkCurrentTournament = () => {
    if (!tournament || tournament.isArchived || tournament.status === 'done') return;
    // Preview (never started, status 'ready'):
    //  - From a registration activity → discard silently (it lives in the activity), save groups.
    //  - Created directly (without registration) → PARK as «klar til start» so it isn't lost.
    if (tournament.status === 'ready') {
      if (tournament.previewActivityId) {
        savePreviewGroupsToActivity(tournament);
        return;
      }
      const readyId =
        tournament.id || `paused_${Date.now()}_${Math.floor(Math.random() * 1000000)}`;
      const parkedReady = {
        ...tournament,
        id: readyId,
        pausedAt: Date.now(),
        log: [...(tournament.log || []), logEntry('parkedReadyNewTournament')],
      };
      const readyList = [parkedReady, ...pausedTournaments.filter((p) => p.id && p.id !== readyId)];
      setPausedTournaments(readyList);
      persistPaused(readyList);
      setToast({
        type: 'success',
        message: `${tournament.config?.name || 'Forrige turnering'} ble lagret under «Pausede turneringer».`,
      });
      return;
    }

    // Corrupt tournament — delete silently. Only Padel24 (slot format) uses
    // groups; flat formats (americano etc.) and club championship do NOT,
    // so they shouldn't be counted as corrupt just because they lack groups.
    const needsGroups = tournament.type === 'padel24' || tournament.type == null;
    const isBroken =
      !Array.isArray(tournament.matches) ||
      tournament.matches.length === 0 ||
      (needsGroups && (!Array.isArray(tournament.groups) || tournament.groups.length === 0));
    if (isBroken) {
      console.log('[Padel24] Ufullstendig turnering forkastet ved start av ny');
      return;
    }

    const ensuredId =
      tournament.id || `paused_${Date.now()}_${Math.floor(Math.random() * 1000000)}`;
    // Preserve «i gang» status: a running tournament that is set aside (because a
    // new one is started) should still show as «Pågår» on the main page — not paused.
    // Only an explicit pause yields status 'paused'.
    const keepRunning = ['running', 'playing', 'finals'].includes(tournament.status);
    const paused = {
      ...tournament,
      id: ensuredId,
      status: keepRunning ? tournament.status : 'paused',
      pausedAt: Date.now(),
      log: [
        ...(tournament.log || []),
        logEntry(keepRunning ? 'setAsideRunningNewTournament' : 'autoParkedNewTournament'),
      ],
    };
    const newList = [paused, ...pausedTournaments.filter((p) => p.id && p.id !== ensuredId)];
    setPausedTournaments(newList);
    persistPaused(newList);
    setToast({
      type: 'success',
      message: `${tournament.config?.name || 'Forrige turnering'} ${keepRunning ? 'er fortsatt i gang på hovedsiden' : 'ble automatisk parkert'}.`,
    });
  };
  useEffect(() => {
    if (tournament?.previewActivityId && tournament.status === 'running') {
      const actId = tournament.previewActivityId;
      // Stash the activity on the tournament BEFORE it is removed, so «Tilbake til klar
      // til start» can reopen the registration later (before anything is played).
      const act = (activities || []).find((a) => a.id === actId) || null;
      // #276, in an EFFECT. This fired unawaited with the error discarded —
      // safe only while the write could not fail. It can now, so the failure is
      // retried briefly and then reported, which is the only surface an effect
      // has. The tournament transition below is deliberately NOT blocked on it:
      // the preview activity has already been consumed into the tournament, and
      // refusing to show that would strand the operator mid-flow with a
      // tournament that exists and a screen that says it does not.
      void saveWithRetry(() =>
        activityCommandsAreServerAuthoritative()
          ? deleteActivities({ ids: [actId], releaseBookings: false })
          : persistActivities((activities || []).filter((a) => a.id !== actId)),
      ).catch((e) =>
        showToast('error', `Kunne ikke rydde bort påmeldingen: ${errMsg(e) || 'ukjent feil'}`),
      );
      setTournament((prev) =>
        prev && prev.previewActivityId === actId
          ? {
              ...prev,
              previewActivityId: undefined,
              sourceActivitySnapshot: act || prev.sourceActivitySnapshot || null,
            }
          : prev,
      );
    }
    // activities is a real data dep (the effect reads + re-persists it); the
    // disable covers the stable setters (persistActivities/setTournament). The
    // previewActivityId guard makes the extra activities-triggered runs no-ops
    // once the commit has cleared the flag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournament?.status, tournament?.previewActivityId, activities]);
  const goHome = () => {
    // Did we get here from a player profile (opened a tournament from there)? Then go back there.
    if (profileReturn) {
      const { view: prevView, playerId } = profileReturn;
      setProfileReturn(null);
      setView(prevView || 'home');
      if (playerId) setProfilePlayerId(playerId);
      return;
    }
    setView('home');
  };
  const archiveTournament = async (t) => {
    if (!t) return { success: false, error: 'Ingen turnering å lagre' };
    const id = t.archiveId || `t_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
    // Whether THIS call creates the archive entry. Only then may a failed ELO
    // commit delete the blob again — see the rollback below.
    const isNewArchiveEntry = !t.archiveId;
    const archivedData = {
      ...t,
      type: t.type || 'padel24', // default: Padel24 tournament
      archiveId: id,
      archivedAt: t.archivedAt || Date.now(),
      isArchived: true,
    };
    const key = `tournament:${id}`;

    try {
      // ── SETTLE A LEGACY KM/SERIESPILL SESSION FIRST (#405) ─────────────
      //
      // Trigger 2 of the legacy catch-up. A session from before result-time
      // commit is holding contributions no player's rating has received, and
      // archive no longer commits them — so a tournament archived without ever
      // being played again would simply lose them.
      //
      // Idempotent server-side: it claims every match it pays for, so this is
      // a no-op for anything already settled. It runs BEFORE the blob is
      // written (#1241): the server resolves the session from the live
      // documents (`active_tournament_v1`, `paused_tournaments_v1`,
      // `active_series_v1`), never from `tournament:<id>`, so nothing here
      // depends on the archive row — and a refused catch-up then leaves no
      // row behind and nothing to roll back. The blob used to be written first,
      // which left an orphan `tournament:<id>` on failure and, because
      // `archiveId` is adopted only on success, a second one on the retry.
      if (
        ratingCommitsAreServerAuthoritative() &&
        (t.type === 'klubbmesterskap' || t.type === 'seriespill')
      ) {
        try {
          await catchUpSessionRatings(String(t.id ?? ''));
        } catch (e) {
          console.error('[Padel24] Kunne ikke gjøre opp turneringens ELO:', e);
          return { success: false, error: errMsg(e) || 'Kunne ikke gjøre opp turneringens ELO.' };
        }
      }

      const json = JSON.stringify(archivedData);
      const sizeKB = (json.length / 1024).toFixed(1);
      console.log(`[Padel24] Lagrer turnering "${t.config?.name}" (${sizeKB} KB) til ${key}`);

      const result = await Storage.set(key, json);

      if (!result) {
        console.error('[Padel24] Storage.set returnerte null/falsy');
        return {
          success: false,
          error: `Storage returnerte ingen bekreftelse (turneringen er ${sizeKB} KB)`,
        };
      }

      // Verify that it was actually saved
      try {
        const verify = await Storage.get(key);
        if (!verify || !verify.value) {
          console.error('[Padel24] Verifisering feilet — kunne ikke lese tilbake');
          return { success: false, error: 'Lagring kunne ikke verifiseres' };
        }
        console.log('[Padel24] Verifisert: turneringen er lagret og kan leses tilbake');
      } catch (verifyErr) {
        console.warn('[Padel24] Kunne ikke verifisere lagring:', verifyErr);
        // Continue anyway — verification can fail even if the save succeeded
      }

      // Sync allPlayers with ELO + matchesPlayed + lastMatchAt from the archived tournament.
      // BUG FIX: previously ONLY elo was synced — this caused new players to
      // stay at matchesPlayed=0 in the global player DB forever, and therefore get
      // the beginner boost again and again. Now all 3 newbie-tracking fields are synced
      // so the boost actually fades out over several tournaments (threshold 20 matches).
      //
      // THE ELO COMMIT GATES THE ARCHIVE (#273 — the same defect #248 fixed for the
      // seriespill season transition). Both branches below used to build the updated
      // list INSIDE a setAllPlayers updater and fire persistPlayers from there without
      // awaiting it, logging a rejection to the console. Three problems: an updater
      // must be pure, and React may invoke it more than once, so the write could fire
      // twice; nothing waited for it; and a failure was swallowed while the tournament
      // stayed archived — irreversibly, from the user's point of view — with every
      // player's rating silently left where it was and the deltas surviving only inside
      // the archived tournament object. Nothing retries and nothing flags the mismatch.
      //
      // Now the list is built OUTSIDE the updater from the LATEST players, the write
      // is awaited, and a failure rolls the archive blob back and returns an error —
      // which every caller already surfaces as a toast — so the operator can retry.
      // Since #795 this is the IN-MEMORY path only (below).
      //
      // ── ARCHIVE CHANGES NO RATINGS (#795) ───────────────────────────
      //
      // Owner decision 2026-10-31, option 1A (delegated to Claude, top chat):
      // archiving a tournament no longer moves a rating. Only results recorded
      // through /tournament-results, and its catch-up for legacy KM and
      // seriespill sessions, do. Both are computed by the SERVER from the
      // club's own recorded matches and claimed per match.
      //
      // This used to measure `tournamentPlayer.elo − globalPlayer.elo` here and
      // send the difference to POST /players/rating-commit, which the server
      // applied as given: a number from a browser, able to move any player the
      // session listed (#795), and a subtraction that removed whatever the
      // player earned elsewhere during the tournament's lifetime
      // (RATING_WRITE_PATHS.md finding 2). That route now answers 410.
      let updatedPlayers: any[] | null = null;
      if (ratingCommitsAreServerAuthoritative()) {
        // The legacy KM/seriespill catch-up (#405) already ran, above, before
        // the blob was written — so the list read here carries the ratings it
        // moved.
        //
        // The server is authoritative about ratings, so the list is read back
        // to show them. A failed read does not fail the archive: archiving
        // commits nothing, so there is nothing a stale list could double.
        try {
          updatedPlayers = await fetchPlayers();
        } catch (e) {
          console.warn('[Padel24] Kunne ikke lese spillerlisten etter arkivering:', e);
        }
      } else {
        // ── THE IN-MEMORY / BLOB PATH ONLY ─────────────────────────────
        //
        // With no server there is no result-time commit, so archive is still
        // where a tournament's ratings reach the player list. The arithmetic
        // below is unchanged.
        const measuredAgainst = allPlayersRef.current || allPlayers;
        const eloDelta = new Map(),
          mcDelta = new Map(),
          ltMap = new Map();
        if (Array.isArray(t.players)) {
          const tournamentPlayerMap = new Map();
          t.players.forEach((p) =>
            tournamentPlayerMap.set(p.id, {
              elo: p.elo,
              matchesPlayed: p.matchesPlayed,
              lastMatchAt: p.lastMatchAt,
            }),
          );

          updatedPlayers = measuredAgainst.map((p) => {
            const tp = tournamentPlayerMap.get(p.id);
            if (!tp) return p;
            // THE TOURNAMENT HOLDS ABSOLUTE VALUES, so the DELTA is measured here
            // rather than sent as a rating. `tp.elo` is this player's rating when
            // the tournament started plus everything it added — a number that is
            // stale the moment another club finishes a match. The difference
            // against the copy this browser measured against is not: the server
            // adds it to whatever the player is rated now.
            //
            // Uncontended the two are identical, because the value the server
            // holds IS the one measured against. The arithmetic is unchanged.
            if (tp.elo != null) eloDelta.set(p.id, tp.elo - (p.elo ?? 0));
            if (tp.matchesPlayed != null)
              mcDelta.set(p.id, tp.matchesPlayed - (p.matchesPlayed ?? 0));
            if (tp.lastMatchAt != null) ltMap.set(p.id, tp.lastMatchAt);
            // Keep the global value if the tournament has none — the tournament
            // always has an updated value after the first applyEloUpdate call, but
            // a defensive check just to be safe.
            return {
              ...p,
              elo: tp.elo != null ? tp.elo : p.elo,
              matchesPlayed: tp.matchesPlayed != null ? tp.matchesPlayed : p.matchesPlayed,
              lastMatchAt: tp.lastMatchAt != null ? tp.lastMatchAt : p.lastMatchAt,
            };
          });
        } else if (t.type === 'klubbmesterskap' || t.type === 'seriespill') {
          // ── THE BLOB PATH ONLY, SINCE #279/#405 ────────────────────────
          //
          // ARCHIVE NO LONGER APPLIES ELO FOR THESE TWO FORMATS on the server
          // path. Their contributions are committed as each result is recorded,
          // and a session predating that transition is settled by the catch-up
          // called above — which claims every match it pays for, so summing them
          // here as well would apply the whole tournament a second time. That
          // doubling is exactly what #279 reported, and the prompted
          // resume→play→re-archive flow reached it.
          //
          // In memory there is no server to commit anything, so the blob path
          // keeps the behaviour it has always had.
          //
          // Sum the delta + match count per player and commit to the global
          // player DB, so the ELO change becomes permanent even after archiving.
          const pairById = new Map((t.pairs || []).map((p) => [p.id, p]));
          (t.matches || []).forEach((m) => {
            if (!m.score || m.score.walkover || !m.teamEloChange) return;
            const ts = m.completedAt || 0;
            const apply = (pair, change) => {
              if (!pair || change == null) return;
              [pair.p1Id, pair.p2Id].filter(Boolean).forEach((pid) => {
                eloDelta.set(pid, (eloDelta.get(pid) || 0) + change);
                mcDelta.set(pid, (mcDelta.get(pid) || 0) + 1);
                if (ts > (ltMap.get(pid) || 0)) ltMap.set(pid, ts);
              });
            };
            apply(pairById.get(m.team1?.pairId), m.teamEloChange.t1);
            apply(pairById.get(m.team2?.pairId), m.teamEloChange.t2);
          });
          if (eloDelta.size > 0) {
            updatedPlayers = measuredAgainst.map((p) => {
              const d = eloDelta.get(p.id),
                mc = mcDelta.get(p.id),
                lt = ltMap.get(p.id);
              if (d == null && mc == null) return p;
              return {
                ...p,
                elo: (p.elo || 0) + (d || 0),
                matchesPlayed: (p.matchesPlayed || 0) + (mc || 0),
                lastMatchAt: lt > (p.lastMatchAt || 0) ? lt : p.lastMatchAt,
              };
            });
          }
        }

        if (updatedPlayers) {
          try {
            await persistPlayers(updatedPlayers);
          } catch (e) {
            console.error('[Padel24] Kunne ikke synce players_v1 etter arkivering:', e);
            // Roll the archive back. The blob IS the archive — the list is rebuilt at
            // mount by prefix-listing `tournament:` (useArchive) — so leaving it behind
            // would archive the tournament without its ratings, which is exactly the
            // outcome this gate exists to prevent. Only a blob THIS call created is
            // removed: when the tournament was already archived (t.archiveId set) the
            // key existed beforehand and deleting it would destroy the earlier record.
            let rolledBack = false;
            if (isNewArchiveEntry) {
              try {
                const del = await Storage.delete(key);
                rolledBack = !!del?.deleted;
              } catch (delErr) {
                console.error('[Padel24] Kunne ikke rulle tilbake arkiveringen:', delErr);
              }
            }
            return {
              success: false,
              error: rolledBack
                ? 'Kunne ikke lagre spillernes ELO — turneringen ble ikke arkivert. Prøv igjen.'
                : 'Kunne ikke lagre spillernes ELO. Turneringen ligger i arkivet, men spillernes ELO er ikke oppdatert — prøv å arkivere på nytt.',
            };
          }
        }
      }

      // Only now — with the ratings safely committed — does the tournament become
      // archived as far as the app is concerned.
      setArchive((prev) => {
        const without = prev.filter((item) => item.key !== key);
        return [{ key, data: archivedData }, ...without];
      });
      if (updatedPlayers) setAllPlayers(updatedPlayers);

      setTournament((curr) => {
        if (!curr) return curr;
        if (
          curr.archiveId === id ||
          curr === t ||
          (curr.config && t.config && curr.config.name === t.config.name && !curr.archiveId)
        ) {
          return { ...curr, archiveId: id, archivedAt: archivedData.archivedAt, isArchived: true };
        }
        return curr;
      });

      return { success: true, key, sizeKB };
    } catch (e) {
      console.error('[Padel24] Lagring feilet:', e);
      const msg = errMsg(e) || 'Ukjent feil';
      const json = JSON.stringify(archivedData);
      const sizeKB = (json.length / 1024).toFixed(1);
      let userMessage = msg;
      if (msg.includes('quota') || msg.includes('size') || msg.includes('5MB')) {
        userMessage = `Turneringen er for stor (${sizeKB} KB) — lagring tillater maks 5 MB per turnering`;
      }
      return { success: false, error: userMessage };
    }
  };
  const resetTournament = () => {
    setTournament(null);
    setView('home');
  };
  const navigateWithAutoPause = (nextView) => {
    // If we are not INSIDE a tournament, just switch view
    const inTournamentView = isLiveTournamentView(view);
    if (!inTournamentView) {
      setView(nextView);
      return;
    }
    // If the tournament is archived or done: just switch view (no parking)
    if (!tournament || tournament.isArchived || tournament.status === 'done') {
      if (tournament?.isArchived) setTournament(null);
      setView(nextView);
      return;
    }
    // Preview (not started, 'ready'): discard/save the preview via the guard.
    if (tournament.status === 'ready') {
      handlePauseTournament();
      if (nextView !== 'home') {
        setTimeout(() => setView(nextView), 0);
      }
      return;
    }
    // Started tournament ('running'/'finals'/'paused'): SET ASIDE in the list
    // (parkCurrentTournament preserves running status). This way ALL running
    // tournaments show alike in the «Pågående» list on home — several can sit there at once.
    parkCurrentTournament();
    setTournament(null);
    setView(nextView);
  };
  const persistPaused = async (list) => {
    try {
      await Storage.set('paused_tournaments_v1', JSON.stringify(list));
    } catch (e) {
      // Not swallowed any more (#1305): the person is told, and offered the
      // stored list instead of a screen that silently disagrees with it.
      reportTournamentSaveFailure('paused_tournaments_v1', e);
    }
  };
  // The four pad-driven result/live-score writers are shared with the physical
  // Banepad device route — extracted VERBATIM into an effect-free hook so the
  // kiosk can reuse them without this hook's lifecycle effects (PR: device pad
  // actions). `persistPaused` (this hook's private paused-list writer) is injected.
  const {
    recordResultForTournament,
    recordKMResultForTournament,
    recordKMLiveScore,
    recordPadLiveScore,
  } = useTournamentResultRecording({
    tournament,
    setTournament,
    setPausedTournaments,
    effectivePlayerDB,
    persistPaused,
  });
  const handlePauseTournament = () => {
    if (!tournament) return;
    // Preview (status 'ready', never started).
    if (tournament.status === 'ready') {
      const actId = tournament.previewActivityId;
      // From a registration activity: keep the activity, discard the preview silently.
      if (actId) {
        savePreviewGroupsToActivity(tournament);
        setTournament(null);
        setActivitiesOpenId(actId);
        setView('activities');
        return;
      }
      // Created directly (without registration): PARK as «klar til start» so it
      // can be resumed from «Pausede turneringer». Without this, a not-started
      // tournament would be lost unless it was started first.
      const readyId =
        tournament.id || `paused_${Date.now()}_${Math.floor(Math.random() * 1000000)}`;
      const parkedReady = {
        ...tournament,
        id: readyId,
        // keep status 'ready' → still «klar til start» on resume
        pausedAt: Date.now(),
        log: [...(tournament.log || []), logEntry('parkedReady')],
      };
      const readyList = [parkedReady, ...pausedTournaments.filter((p) => p.id && p.id !== readyId)];
      setPausedTournaments(readyList);
      persistPaused(readyList);
      setTournament(null);
      setView('home');
      setToast({
        type: 'success',
        message: `${tournament.config?.name || 'Turneringen'} ble lagret under «Pausede turneringer». Gjenoppta og start den når du vil.`,
      });
      return;
    }
    // Ensure the paused tournament has a UNIQUE id, even if the engine didn't set one.
    // Without this, several demos/americanos could collide on an undefined id and overwrite each other.
    const ensuredId =
      tournament.id || `paused_${Date.now()}_${Math.floor(Math.random() * 1000000)}`;
    const paused = {
      ...tournament,
      id: ensuredId,
      status: 'paused',
      pausedAt: Date.now(),
      log: [...(tournament.log || []), logEntry('pausedParked')],
    };
    // Filter out any existing entry with the same id (safety net).
    // Requires an id match to filter, so undefined ids don't block new ones.
    const newList = [paused, ...pausedTournaments.filter((p) => p.id && p.id !== ensuredId)];
    setPausedTournaments(newList);
    persistPaused(newList);
    setTournament(null);
    setView('home');
    setToast({
      type: 'success',
      message: `${tournament.config?.name || 'Turneringen'} ble parkert. Du kan starte en ny eller gjenoppta senere.`,
    });
  };
  const handleResumeTournament = (id) => {
    const target = pausedTournaments.find((p) => p.id === id);
    if (!target) return;

    // If there is an active tournament, park it first
    let newPaused = pausedTournaments.filter((p) => p.id !== id);
    if (tournament && !tournament.isArchived) {
      const ensuredId =
        tournament.id || `paused_${Date.now()}_${Math.floor(Math.random() * 1000000)}`;
      // Preserve «i gang» status so the previously active one still shows as «Pågår».
      const keepRunning = ['running', 'playing', 'finals'].includes(tournament.status);
      const current = {
        ...tournament,
        id: ensuredId,
        status: keepRunning ? tournament.status : 'paused',
        pausedAt: Date.now(),
        log: [
          ...(tournament.log || []),
          logEntry(keepRunning ? 'setAsideRunningSwitched' : 'autoParkedSwitched'),
        ],
      };
      newPaused = [current, ...newPaused.filter((p) => p.id && p.id !== ensuredId)];
    }

    setPausedTournaments(newPaused);
    persistPaused(newPaused);

    // Resume target — set to playing or running depending on type
    const wasAmericanoOrFlat =
      target.type === 'americano' ||
      target.type === 'mexicano' ||
      target.type === 'mixicano' ||
      target.type === 'team_mexicano' ||
      target.type === 'mix_americano' ||
      target.type === 'club_americano' ||
      target.type === 'club_team_americano' ||
      target.type === 'club_mexicano' ||
      !target.config?.phases;
    const resumed = {
      ...target,
      status: wasAmericanoOrFlat ? 'playing' : 'running',
      pausedAt: null,
      resumedAt: Date.now(),
      log: [...(target.log || []), logEntry('resumedFromParked')],
    };
    setTournament(resumed);

    // Navigate to the right view
    if (target.type === 'americano') setView('americano');
    else if (target.type === 'mexicano') setView('mexicano');
    else if (target.type === 'mixicano') setView('mixicano');
    else if (target.type === 'team_mexicano') setView('teamMexicano');
    else if (target.type === 'mix_americano') setView('mixAmericano');
    else if (target.type === 'club_americano') setView('clubAmericano');
    else if (target.type === 'club_team_americano') setView('clubTeamAmericano');
    else if (target.type === 'club_mexicano') setView('clubMexicano');
    else if (target.type === 'klubbmesterskap') setView('klubbmesterskap');
    else setView('tournament');

    setToast({
      type: 'success',
      message: `${target.config?.name || 'Turneringen'} ble gjenopptatt.`,
    });
  };
  const handlePauseRunningInList = (id) => {
    const newList = pausedTournaments.map((p) =>
      p.id === id
        ? {
            ...p,
            status: 'paused',
            pausedAt: Date.now(),
            log: [...(p.log || []), logEntry('pausedFromHome')],
          }
        : p,
    );
    setPausedTournaments(newList);
    persistPaused(newList);
    setToast({ type: 'success', message: 'Turneringen er satt på pause.' });
  };
  const handleArchiveFromList = async (id) => {
    const entry = pausedTournaments.find((p) => p.id === id);
    if (!entry) return;
    const result = await archiveTournament(entry);
    if (result.success) {
      // Functional updater: read the LATEST paused list (not the stale closure
      // snapshot captured before the await) so a concurrent change isn't clobbered.
      setPausedTournaments((prev) => {
        const newList = prev.filter((p) => p.id !== id);
        persistPaused(newList);
        return newList;
      });
      setToast({
        type: 'success',
        message: `${entry.config?.name || 'Turneringen'} er lagret i arkivet.`,
      });
    } else {
      setToast({ type: 'error', message: result.error || 'Kunne ikke lagre turneringen.' });
    }
  };
  const handleDeletePaused = (id) => {
    const newList = pausedTournaments.filter((p) => p.id !== id);
    setPausedTournaments(newList);
    persistPaused(newList);
    setToast({ type: 'success', message: 'Pauset turnering slettet.' });
  };
  const handleDeleteAllPaused = () => {
    const count = pausedTournaments.length;
    setPausedTournaments([]);
    persistPaused([]);
    setToast({ type: 'success', message: `${count} pausede turneringer slettet.` });
  };
  const openArchived = (data) => {
    setProfileReturn(null); // reset here; the profile path sets it again afterwards
    if (data?.isVirtual && data?.config?.tournamentType === 'single_matches') {
      // Remember where the user came from (typically the archive or the front page)
      setSingleMatchesReturnView(view === 'archive' ? 'archive' : 'home');
      setView('singleMatches');
      return;
    }
    if (data?.type === 'seriespill') {
      // Finished season from the archive list → open the dedicated season-history view
      // focused on exactly this season (class + index in seriesHistory).
      if (data.isSeriesSeason && data.seasonClassId && data.seasonHistIdx != null) {
        setSerieHistoryFocus({ classId: data.seasonClassId, idx: data.seasonHistIdx });
        return;
      }
      // Active series is rendered by SeriespillView via activeSeries — just switch view.
      // (The profile wrapper is not the series object itself, so we don't touch tournament.)
      setView('seriespill');
      return;
    }
    setTournament({ ...data, isArchived: true });
    // Route to the right view based on tournament type.
    // Padel24 tournaments use the slot format and require the 'tournament' view;
    // Americano (and future Mexicano) use the flat format and require their own view.
    if (data?.type === 'americano') {
      setView('americano');
    } else if (data?.type === 'mexicano') {
      setView('mexicano');
    } else if (data?.type === 'mixicano') {
      setView('mixicano');
    } else if (data?.type === 'team_mexicano') {
      setView('teamMexicano');
    } else if (data?.type === 'mix_americano') {
      setView('mixAmericano');
    } else if (data?.type === 'club_americano') {
      setView('clubAmericano');
    } else if (data?.type === 'club_team_americano') {
      setView('clubTeamAmericano');
    } else if (data?.type === 'club_mexicano') {
      setView('clubMexicano');
    } else if (data?.type === 'klubbmesterskap') {
      setView('klubbmesterskap');
    } else {
      setView('tournament');
    }
  };
  const resumeArchivedTournament = () => {
    if (!tournament || !tournament.isArchived) return;
    // The user ALWAYS wants it back in active state (even if it was
    // paused before archiving). Flat formats use 'playing', the rest 'running';
    // finals are kept as finals.
    const flatFormat = [
      'americano',
      'mexicano',
      'mixicano',
      'team_mexicano',
      'mix_americano',
      'club_americano',
      'club_team_americano',
      'club_mexicano',
    ].includes(tournament.type);
    const nextStatus =
      tournament.status === 'finals' ? 'finals' : flatFormat ? 'playing' : 'running';
    setTournament((prev) =>
      prev
        ? {
            ...prev,
            isArchived: false,
            status: nextStatus,
            log: [...(prev.log || []), logEntry('restoredFromArchive')],
          }
        : prev,
    );
    showToast(
      'success',
      'Turneringen er aktiv igjen og vises som «Pågår» på hovedsiden. Lagre på nytt når du er ferdig.',
    );
  };

  return {
    handleWizardComplete,
    savePreviewGroupsToActivity,
    parkCurrentTournament,
    goHome,
    archiveTournament,
    resetTournament,
    navigateWithAutoPause,
    recordResultForTournament,
    recordKMResultForTournament,
    recordKMLiveScore,
    recordPadLiveScore,
    handlePauseTournament,
    handleResumeTournament,
    handlePauseRunningInList,
    handleArchiveFromList,
    handleDeletePaused,
    handleDeleteAllPaused,
    openArchived,
    resumeArchivedTournament,
  };
}
